import {OperationVariables} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {ReactNode, useEffect} from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {AccessibleGraphQLObject, getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList, {getChipsRowHeight} from 'ui-library/chips/ChipList';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ErrorIcon from 'ui-library/icons/ErrorIcon';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
} from 'ui-library/scrollable/ScrollableTableConstants';

import {InaccessibleInfoGraphQL} from '../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

type ContainerNode = AccessibleGraphQLObject & {
  __typename: 'ContainerNode';
  barcode: {value: string} | null;
  contents: Array<{
    containedItem: AccessibleGraphQLObject | InaccessibleInfoGraphQL;
  }>;
};
type BioentityNode = AccessibleGraphQLObject & {
  __typename:
    | 'AaSequenceNode'
    | 'BasicFolderItemNode'
    | 'BatchNode'
    | 'ComplexPolymerNode'
    | 'EquipmentNode'
    | 'MixtureNode'
    | 'MoleculeNode'
    | 'OligoConjugateNode'
    | 'OligoDuplexNode'
    | 'PlateNode'
    | 'ProteinNode'
    // Replacement name for ProteinNode; both are reachable until the old one is removed
    // (BNCH-169122).
    | 'AaSequenceNode'
    | 'SequenceNode';
};
type OtherNode = AccessibleGraphQLObject & {
  __typename: 'BatchNode' | 'PlateNode';
};
export type ItemNode = ContainerNode | BioentityNode | OtherNode | InaccessibleInfoGraphQL;
export interface SelectItemsAdditionalColumn<TItemNode extends ItemNode> {
  // Table column definition
  column: ScrollableTableColumn;
  // Function to get the value of this column for the given item
  accessor: (itemNode: TItemNode) => ReactNode;
  // Which nodetypes this column is applicable to.
  // Column will only be shown if any items are one of these types, or always shown if nodeTypes is undefined
  nodeTypes?: Set<ItemNode['__typename']>;
}

const ITEM_COLUMN: ScrollableTableColumn = {
  header: 'Item',
  accessorKey: 'itemDisplay',
  isCollapsible: false,
  isSortable: false,
  flexGrow: 1,
};
const BARCODE_COLUMN: ScrollableTableColumn = {
  header: 'Barcode',
  accessorKey: 'barcode',
  isCollapsible: false,
  isSortable: false,
};
const CONTENTS_COLUMN: ScrollableTableColumn = {
  header: 'Contents',
  accessorKey: 'contentsDisplay',
  isCollapsible: false,
  isSortable: false,
  flexGrow: 1,
};

const CONTAINER_COLUMNS = [CONTENTS_COLUMN, BARCODE_COLUMN];

export interface ListItemValidation {
  isSelectorDisabled: boolean;
  tooltipText?: string;
}

interface ItemData<TItemNode extends ItemNode> {
  item: TItemNode;
  itemDisplay: JSX.Element;
  barcode?: string;
  contents?: Array<string>;
  contentsDisplay?: JSX.Element;
  [accessorKey: string]: ReactNode;
}

interface ItemRow<TItemNode extends ItemNode> extends ScrollableTableRow {
  data: ItemData<TItemNode>;
}

function isContainerNode(itemNode: ItemNode): itemNode is ContainerNode {
  return itemNode.__typename === 'ContainerNode';
}

/**
 * Build a scrollable table row for the given item.
 * @param itemNode
 * @param selectedIds
 * @param validateItem
 */
function getItemRow<TItemNode extends ItemNode>({
  itemNode,
  selectedItemIds,
  validateItem,
  additionalColumns,
}: {
  itemNode: TItemNode;
  selectedItemIds: Set<string>;
  validateItem: (itemNode: TItemNode) => ListItemValidation;
  additionalColumns: Array<SelectItemsAdditionalColumn<TItemNode>>;
}): ItemRow<TItemNode> {
  const itemId = getAccessibleOrInaccessibleId(itemNode);
  const {isSelectorDisabled, tooltipText} = validateItem(itemNode);
  const itemDisplay = (
    <div className={css(styles.flex)}>
      <div className={css(styles.overflow)}>
        <BenchChip id={itemId} size={ChipSize.TABLE_CELL} isLink={false} />
      </div>
      {isSelectorDisabled && (
        <Tooltip text={tooltipText} placement={FloatingPlacement.TOP}>
          <ErrorIcon />
        </Tooltip>
      )}
    </div>
  );

  const itemData: ItemData<TItemNode> = {
    item: itemNode,
    itemDisplay,
    ...Object.fromEntries(
      additionalColumns.map(({column, accessor}) => [column.accessorKey, accessor(itemNode)])
    ),
  };

  if (isContainerNode(itemNode)) {
    const barcode = itemNode.barcode?.value ?? '';
    const contentIds = itemNode.contents.map((content) =>
      getAccessibleOrInaccessibleId(content.containedItem)
    );
    return {
      id: itemId,
      isSelectorDisabled,
      isSelected: selectedItemIds.has(itemId),
      data: {
        ...itemData,
        barcode,
        contents: contentIds,
        contentsDisplay: (
          <ChipList itemIds={contentIds} chipSize={ChipSize.TABLE_CELL} showChipsAsLinks={false} />
        ),
      },
    };
  }
  return {
    id: itemId,
    isSelectorDisabled,
    isSelected: selectedItemIds.has(itemId),
    data: itemData,
  };
}

/**
 * Renders a table of items with a selectable checkbox.
 * @param items
 * @param selectedItems
 * @param setSelectedItems
 * @param validateItem
 * @constructor
 */
export function SelectableItemsTable<TItemNode extends ItemNode>({
  items,
  selectedItems,
  setSelectedItems,
  validateItem,
  additionalColumns,
}: {
  items: Array<TItemNode>;
  selectedItems: Array<TItemNode>;
  setSelectedItems: (selectedItems: Array<TItemNode>) => void;
  validateItem: (itemNode: TItemNode) => ListItemValidation;
  additionalColumns?: Array<SelectItemsAdditionalColumn<TItemNode>>;
}): JSX.Element {
  const selectedItemIds = new Set(selectedItems.map((item) => getAccessibleOrInaccessibleId(item)));

  useEffect(() => {
    // If there are already items selected, do not auto-select items, but use the provided selection
    // instead.
    if (selectedItemIds.size === 0) {
      setSelectedItems(itemRows.filter((r) => !r.isSelectorDisabled).map((r) => r.data.item));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const itemRows = items.map((itemNode) =>
    getItemRow<TItemNode>({
      itemNode,
      selectedItemIds,
      validateItem,
      additionalColumns: additionalColumns ?? [],
    })
  );

  const columns = [ITEM_COLUMN];
  if (items.some((i) => isContainerNode(i))) {
    columns.push(...CONTAINER_COLUMNS);
  }
  for (const {column, nodeTypes} of additionalColumns ?? []) {
    if (nodeTypes == null || items.some((i) => nodeTypes.has(i.__typename))) {
      columns.push(column);
    }
  }

  function getRowContentHeight(rowIndex: number): number {
    // Default to 1 chip, but if the row is a non-empty container, use the number of contents instead.
    let numChips = 1;
    const row = itemRows[rowIndex];
    // Per ScrollableTable.getRowHeight we should always handle rowIndex = 0, even if itemRows is emtpy.
    if (row != null && isContainerNode(row.data.item) && row.data.item.contents.length > 0) {
      numChips = row.data.item.contents.length;
    }

    return getChipsRowHeight(numChips, ChipSize.TABLE_CELL);
  }

  return (
    <ScrollableTable
      columns={columns}
      rows={itemRows}
      rowSelectionMode={RowSelectionMode.MULTIPLE}
      allowOverflow={false}
      emptyTableText="No items in list"
      getRowContentHeight={getRowContentHeight}
      matchParentHeight={false}
      hasLineBelowHeader={false}
      isCompact={true}
      canResizeColumns={false}
      canFilterColumns={false}
      onRowsSelect={(selectedRows: Array<ItemRow<TItemNode>>, isSelected: boolean) => {
        const toggleIds = new Set(selectedRows.map((r) => r.id));
        const selectedItems = itemRows
          .filter((itemRow) =>
            isSelected
              ? (toggleIds.has(itemRow.id) || itemRow.isSelected) && !itemRow.isSelectorDisabled
              : !toggleIds.has(itemRow.id) && itemRow.isSelected
          )
          .map((r) => r.data.item);
        setSelectedItems(selectedItems);
      }}
      fixedRowCount={0}
    />
  );
}

export interface SelectItemsFromListViewProps<
  TListItemsQuery,
  TListSummary,
  TQueryVariables extends OperationVariables,
> {
  /** Whether the search component should take up the full width of the view. Default: 50% */
  fullWidthSearchInput?: boolean;
  /** Renderer for the list search component. */
  listSearchInput: JSX.Element;
  /** Currently selected list summary. */
  listSummary: TListSummary | null;
  /** Query to fetch list items from a selected list. */
  query: DocumentNode;
  /** Function to compute query variables from the selected list. */
  queryVariablesFromListSummary: (listSummary: TListSummary) => TQueryVariables;
  /** Child component to render the list items, accepting the data returned from the query. */
  children: (data: TListItemsQuery) => JSX.Element;
}

export function SelectItemsFromListView<
  TListItemsQuery,
  TListSummary,
  TQueryVariables extends OperationVariables,
>({
  fullWidthSearchInput,
  listSearchInput,
  listSummary,
  query,
  queryVariablesFromListSummary,
  children,
}: SelectItemsFromListViewProps<TListItemsQuery, TListSummary, TQueryVariables>): JSX.Element {
  return (
    <>
      <div
        className={css(
          styles.selector,
          fullWidthSearchInput ? styles.fullWidthSelector : styles.halfWidthSelector
        )}
      >
        {listSearchInput}
      </div>
      {listSummary && (
        <BenchlingQuery<TListItemsQuery, TQueryVariables>
          query={query}
          variables={queryVariablesFromListSummary(listSummary)}
          renderLoading={() => <CenteredHorizontalThrobber />}
        >
          {(data): JSX.Element => children(data)}
        </BenchlingQuery>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  halfWidthSelector: {
    width: '50%',
  },
  fullWidthSelector: {
    width: '100%',
  },
  selector: {
    marginBottom: 16,
  },
  flex: {
    display: 'flex',
    gap: '4px',
  },
  overflow: {
    overflow: 'hidden',
  },
});
